import type { PezTransformApi } from '@/api/types/photo-effects'
import type { PhotoEffectDefinition } from '@/lib/photo-effects/types/photo-effect-definition'
import type { PhotoEffectId } from '@/lib/photo-effects/types/photo-effect-id'
import type { PhotoEffectRuntimeCatalog } from '@/lib/photo-effects/types/photo-effect-runtime-catalog'
import {
  formatPezBackendValue,
  formatSepiaPezBackendValue,
  mapPhotoEffectBackendToUiValue,
  mapPhotoEffectUiToBackendValue,
  parsePezBackendValue,
} from './helpers/photo-effect-range-map'
import { photoEffectIdFromTx, photoEffectTxFromId } from '@/lib/photo-effects/photo-effect-tx'
import type { PhotoEffectsState } from '@/lib/photo-effects/types/photo-effects-state'
import {
  clampPhotoEffectValue,
  createDefaultPhotoEffectsState,
} from '@/lib/photo-effects/photo-effects-state'

function defaultValueFor(def: PhotoEffectDefinition): number {
  return def.defaultValue ?? def.min ?? 0
}

/**
 * Build POST `pezTransforms` from popover UI state.
 * Adjustable effects toggled off after use are sent with `disable: true` (activation order tracks use).
 */
export function buildPezTransformsFromPhotoEffectsState(
  state: PhotoEffectsState,
  catalog: PhotoEffectRuntimeCatalog,
  activationOrder: readonly PhotoEffectId[]
): PezTransformApi[] {
  const activationSet = new Set(activationOrder)
  const transforms: PezTransformApi[] = []

  for (const def of catalog.definitions) {
    const entry = state[def.id]

    if (!entry) continue

    const tx = catalog.txById.get(def.id) ?? photoEffectTxFromId(def.id)
    const pezDef = catalog.pezDefById.get(def.id)

    if (def.kind === 'binary') {
      if (entry.active) {
        transforms.push({ tx })
      }

      continue
    }

    const uiValue = clampPhotoEffectValue(
      def.id,
      entry.value ?? defaultValueFor(def),
      catalog.definitions
    )
    const backendValue = mapPhotoEffectUiToBackendValue(uiValue, pezDef)
    const v =
      def.id === 'sepia'
        ? formatSepiaPezBackendValue(uiValue, pezDef)
        : formatPezBackendValue(backendValue)

    if (entry.active) {
      transforms.push({ tx, v })
      continue
    }

    if (activationSet.has(def.id)) {
      transforms.push({ tx, v, disable: true })
    }
  }

  return transforms
}

export function photoEffectsStateFromPezTransforms(
  transforms: PezTransformApi[],
  catalog: PhotoEffectRuntimeCatalog
): { state: PhotoEffectsState; activationOrder: PhotoEffectId[] } {
  const state = createDefaultPhotoEffectsState(catalog.definitions)
  const activationOrder: PhotoEffectId[] = []

  for (const transform of transforms) {
    const id = photoEffectIdFromTx(transform.tx)

    if (!id) continue

    const def = catalog.definitions.find((d) => d.id === id)

    if (!def) continue

    const pezDef = catalog.pezDefById.get(id)
    const disabled = transform.disable === true

    if (def.kind === 'binary') {
      state[id] = { active: !disabled }
      if (!disabled) activationOrder.push(id)
      continue
    }

    const backend = parsePezBackendValue(transform.v)
    const ui =
      backend != null
        ? clampPhotoEffectValue(
            id,
            mapPhotoEffectBackendToUiValue(backend, pezDef),
            catalog.definitions
          )
        : defaultValueFor(def)

    state[id] = { active: !disabled, value: ui }

    // Keep disabled adjustable effects in activation order so the next POST still sends
    // `{ v, disable: true }` after GET /photos/pez re-hydrate (WMPCM-9498).
    activationOrder.push(id)
  }

  return { state, activationOrder }
}
